import { css } from 'astroturf';
import React from 'react';
import Prism from "prismjs";
import "prismjs/components/prism-markup";
import "prismjs/components/prism-css";
import "prismjs/components/prism-javascript";

const styles = css`
  .codeBlock {
    border-radius: 0.25rem;
    border-width: 1px;
    border-style: solid;
    border-color: var(--sheet-slate-700-50);
    overflow-x: auto;
  }

  .codeText {
    font-size: 11px;
    line-height: 1.25;
  }

  .propertyRow {
    margin-bottom: 0.25rem;
  }

  .propertyLine {
    display: flex;
    align-items: flex-start;
  }

  .propertyName {
    color: var(--sheet-cyan-300);
    font-weight: 600;
    flex-shrink: 0;
  }

  .colon {
    color: var(--sheet-slate-500);
  }

  .values {
    display: flex;
    flex-wrap: wrap;
  }

  .valueToken {
    color: var(--sheet-emerald-300);
    background-color: var(--sheet-slate-900-50);
    padding-left: 0.25rem;
    padding-right: 0.25rem;
    border-radius: 0.25rem;
    font-size: 11px;
    line-height: 1.25;
  }

  .valueDefault {
    text-decoration-line: underline;
    text-decoration-color: var(--sheet-emerald-400-60);
    text-underline-offset: 2px;
  }

  .section {
    margin-top: 0.25rem;
    margin-bottom: 0.25rem;
  }

  .sectionTitle {
    font-size: 12px;
    line-height: 1.35;
    font-weight: 600;
    color: #ffffff;
    margin-bottom: 0.25rem;
    font-family: var(--font-inter);
  }

  .sectionBody {
    display: flex;
    flex-direction: column;
    row-gap: 0.125rem;
  }

  .propertyRow:last-child {
    margin-bottom: 0;
  }

  .section:last-child {
    margin-bottom: 0;
  }
`;

export function CodeBlock({ children, language = "javascript", className = "" }) {
  const grammar = Prism.languages[language] ?? Prism.languages.markup;
  const highlighted = Prism.highlight(children, grammar, language);
  const preClassName = [styles.codeBlock, className, `language-${language}`]
    .filter(Boolean)
    .join(" ");

  return (
    <pre className={preClassName}>
      <code
        className={`${styles.codeText} language-${language}`}
        dangerouslySetInnerHTML={{ __html: highlighted }}
      />
    </pre>
  );
}

export function PropertyRow({ property, values, description, defaultValue }) {
  return (
    <div className={styles.propertyRow}>
      <div className={styles.propertyLine}>
        <code className={styles.propertyName}>{property}</code>
        {values.length > 0 && (
          <>
            <span className={styles.colon}>:</span>
            <div className={styles.values}>
              {values.map((value, i) => (
                <code
                  key={i}
                  className={`${styles.valueToken} ${value === defaultValue ? styles.valueDefault : ""}`}
                >
                  {value}
                </code>
              ))}
            </div>
          </>
        )}
      </div>
      {description && <p className={styles.description}>{description}</p>}
    </div>
  );
}

export function Section({ title, children, className = "" }) {
  return (
    <section className={`${styles.section} ${className}`.trim()}>
      <h2 className={styles.sectionTitle}>
        {title}
      </h2>
      <div className={styles.sectionBody}>{children}</div>
    </section>
  );
}
